import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import PeopleIcon from '@mui/icons-material/People'; // Icon for organization assignment
import ConfirmDialog from '../components/ConfirmDialog';
import UserCommentsModal from '../components/UserCommentsModal';
import UserEditModal from '../components/UserEditModal'; // Create this component later
import { useListAppUsers, useDeleteAppUser } from '../services/appService';

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleString();
    } catch (e) {
        console.error("Error formatting date:", dateInput, e);
        return 'Invalid Date';
    }
}

function UsersPage() {
    const { appId } = useParams();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalItems, setTotalItems] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [selectedUserForComments, setSelectedUserForComments] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);

    const listUsersHook = useListAppUsers();
    const deleteUserHook = useDeleteAppUser();

    const fetchUsers = useCallback(async (page, pageSize) => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listUsersHook(appId, page + 1, pageSize); // API might be 1-based
            setUsers(data.users || []);
            setTotalItems(data.totalItems || 0);
        } catch (err) {
            console.error("Error fetching users:", err);
            setError(err.message || 'Failed to load users.');
            setUsers([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [appId, listUsersHook]);

    useEffect(() => {
        fetchUsers(paginationModel.page, paginationModel.pageSize);
    }, [fetchUsers, paginationModel]);

    const handlePaginationModelChange = (newModel) => {
        setPaginationModel(newModel);
    };

    const handleOpenEditModal = (user) => {
        setSelectedUserForEdit(user);
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setSelectedUserForEdit(null);
        setEditModalOpen(false);
    };

    const handleUserUpdated = () => {
        handleCloseEditModal();
        fetchUsers(paginationModel.page, paginationModel.pageSize); // Refresh data
    }

    const handleOpenCommentsModal = (userId) => {
        setSelectedUserForComments(userId);
        setCommentsModalOpen(true);
    };

    const handleCloseCommentsModal = () => {
        setSelectedUserForComments(null);
        setCommentsModalOpen(false);
    };

    const handleOpenConfirmDialog = (userId) => {
        setUserToDelete(userId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setUserToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete || !appId) return;
        setLoading(true); // Indicate loading state during delete
        try {
            await deleteUserHook(appId, userToDelete);
            // Refresh list only if deletion was successful
            fetchUsers(paginationModel.page, paginationModel.pageSize);
        } catch (err) {
            console.error("Error deleting user:", err);
            setError(err.message || 'Failed to delete user.');
            setLoading(false); // Stop loading on error
        } finally {
            handleCloseConfirmDialog();
             // setLoading(false) is handled in the fetchUsers call on success
        }
    };

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            width: 100,
            renderCell: (params) => (
                <Typography
                    variant="caption"
                    title={params.value}
                    sx={{ fontFamily: 'monospace', color: 'text.secondary', cursor: 'help' }}
                >
                    {params.value?.slice(0, 8)}…
                </Typography>
            ),
        },
        { field: 'display_name', headerName: 'Display Name', width: 200 },
        {
            field: 'email',
            headerName: 'Kontakt / Auth',
            width: 280,
            renderCell: (params) => {
                const row = params.row;
                if (row.is_username_account) {
                    // Username-Account: zeige Nutzername + Verifizierungs-Email/-Telefon
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5, gap: 0.3 }}>
                            {row.username && (
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    @{row.username}
                                </Typography>
                            )}
                            {row.profile_email && (
                                <Typography variant="caption" color="text.secondary">
                                    ✉ {row.profile_email}
                                </Typography>
                            )}
                            {row.phone && (
                                <Typography variant="caption" color="text.secondary">
                                    📱 {row.phone}
                                </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                Auth: {row.supabase_auth_email}
                            </Typography>
                        </Box>
                    );
                }
                // Normaler Email-Account
                return <Typography variant="body2">{row.email}</Typography>;
            },
        },
        {
            field: 'organizations',
            headerName: 'Organizations',
            width: 300,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {params.value?.map((org) => (
                        <Chip key={org.id} label={org.name} size="small" />
                    )) || '-'}
                </Box>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 180,
            type: 'dateTime',
            valueFormatter: (value) => formatDate(value)
        },
        {
            field: 'last_sign_in_at',
            headerName: 'Last Sign In',
            width: 180,
            type: 'dateTime',
            valueFormatter: (value) => formatDate(value)
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 150,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit/Assign Org"
                        onClick={() => handleOpenEditModal(row)} // Pass the whole row
                        color="inherit"
                        title="Edit User / Assign Organizations"
                    />,
                    <GridActionsCellItem
                        icon={<CommentIcon />}
                        label="View Comments"
                        onClick={() => handleOpenCommentsModal(id)}
                        color="inherit"
                        title="View All Comments by User"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete User"
                        onClick={() => handleOpenConfirmDialog(id)}
                        color="inherit"
                        title="Delete User (Irreversible)"
                    />,
                ];
            },
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', width: '100%' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                Manage Users
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ height: 'calc(100% - 80px)', width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    rowCount={totalItems}
                    loading={loading}
                    pageSizeOptions={[5, 10, 25, 50]}
                    paginationModel={paginationModel}
                    paginationMode="server" // Important for server-side pagination
                    onPaginationModelChange={handlePaginationModelChange}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    getRowHeight={() => 'auto'}
                    sx={{
                        border: 0,
                         // Ensure proper height calculation
                        '& .MuiDataGrid-main': { overflow: 'auto' },
                        '& .MuiDataGrid-virtualScroller': {
                            minHeight: '200px', // Adjust as needed
                        },
                         '& .MuiDataGrid-footerContainer': {
                            position: 'sticky', 
                            bottom: 0,
                            backgroundColor: 'white', // or theme background
                            zIndex: 1, 
                        }
                    }}
                />
            </Box>

            {selectedUserForEdit && (
                 <UserEditModal
                    open={editModalOpen}
                    onClose={handleCloseEditModal}
                    onUserUpdated={handleUserUpdated}
                    user={selectedUserForEdit}
                    appId={appId}
                />
            )}

            {selectedUserForComments && (
                <UserCommentsModal
                    open={commentsModalOpen}
                    onClose={handleCloseCommentsModal}
                    userId={selectedUserForComments}
                    appId={appId}
                />
            )}

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete User?"
                message={`Are you sure you want to delete user ID: ${userToDelete}? This action is IRREVERSIBLE and will remove the user from Supabase Auth.`}
            />

        </Box>
    );
}

export default UsersPage; 